import { addSurveyEvent, deleteSurveyEvent, getSurveyEvents, updateSurveyEvent } from "../actions/survey.event.action";
import TableList from "../components/TableList";
import { SurveyEventSubmit, SurveyEvent } from "../models/survey.event.model";
import { useCallback, useEffect, useRef } from "react";
import SurveyEventForm from "./SurveyEventForm";
import { downloadFile, filterOnKeys } from "../lib/helper";
import { FilterGroup, FilterType } from "../components/TableFilter";
import { FormRefEvent, HeadCell } from "../models/base.model";
import { Button, Tooltip } from "@mui/material";
import LoadingButton from "../components/LoadingButton";
import { getSurveys } from "../actions/survey.action";
import { Survey } from "../models/survey.model";
import SurveyCustomers from "./SurveyCustomers";
import { getCompanies } from "../actions/company.action";
import { Company } from "../models/company.model";
import moment from "moment";
import { CompanyStore } from "../models/company.store.model";
import { getCompanyStores, getCompanyStoresByCompanyId } from "../actions/company.store.action";
import JSZip from "jszip";
import QRCode from "qrcode";
import { QrCode2 as QrCode2Icon, Description as DescriptionIcon } from "@mui/icons-material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import SurveyCoupons from "./SurveyCoupons";
import SurveyComplains from "./SurveyComplains";

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    label: "ID",
  },
  {
    id: "survey.name",
    label: "Survey Name",
  },
  {
    id: "company.name",
    label: "Company Name",
  },
  {
    id: "customers",
    label: "Records",
    render: (_, r) => <ViewCustomerRecords key={`records_${r.id}_${(r.customers || []).length}`} record={r} />,
  },
  {
    id: "link",
    label: "Link",
    render: (_, record) => (
      <Tooltip title="Click to copy link">
        <div>
          <LoadingButton
            data-testid={"Link"}
            variant="outlined"
            onClick={async () => {
              await navigator.clipboard.writeText(`${process.env.REACT_APP_SURVEY_URL}/#/survey/${record.id}`);
              window.webApp.showMessage("success", "Successfully copied");
            }}
          >
            Copy
          </LoadingButton>
        </div>
      </Tooltip>
    ),
  },
  {
    id: "createdAt",
    label: "Created At",
    render: (v, record) => <div data-testid={`createdAt`}>{v && moment(v).format("YYYY-MM-DD HH:mm:ss")}</div>,
  },
];

function ViewCustomerRecords(props: { record: SurveyEvent }) {
  const { record } = props;
  const formRef = useRef<FormRefEvent<SurveyEventSubmit[]>>(null);
  return (
    <Button
      variant="contained"
      onClick={() => {
        window.webApp.setTab({
          title: `Survey "${record?.survey?.name}" Event Customers`,
          element: <SurveyCustomers ref={formRef} id={record.id} companyId={record.companyId} />,
          onCancel: async () => {
            if (!formRef.current) {
              throw new Error("Form creation error, please refresh or contact us!");
            }
            await formRef.current.onSubmit();
          },
          cancelText: "Close",
          show: true,
        });
      }}
      data-testid={"Records"}
    >
      View
    </Button>
  );
}

const filters: FilterGroup<SurveyEvent>[] = [
  {
    title: "Filter By Date",
    items: [
      {
        name: "createdAt",
        type: FilterType.date_range,
      },
      {
        name: "updatedAt",
        type: FilterType.date_range,
      },
    ],
  },
];

let surveys: Survey[] | undefined, companies: Company[] | undefined, companyStores: CompanyStore[] | undefined;

export default function SurveyEvents() {
  const formRef = useRef<FormRefEvent<SurveyEvent>>(null);

  async function load(noEvent?: boolean) {
    const [surveyEventsData] = await Promise.all([
      !noEvent ? getSurveyEvents() : Promise.resolve(),
      !surveys ? getSurveys().then(({ data }) => (surveys = data)) : Promise.resolve(),
      !companies ? getCompanies().then(({ data }) => (companies = data)) : Promise.resolve(),
      !companyStores ? getCompanyStores().then(({ data }) => (companyStores = data)) : Promise.resolve(),
    ]);
    if (surveyEventsData) {
      surveyEventsData.data.forEach((d) => {
        d.company = companies?.find((s) => s.id === d.companyId);
        d.survey = surveys?.find((s) => s.id === d.surveyId);
        d.stores = companyStores?.filter((store) => store.companyId === d.companyId);
      });
    }
    return surveyEventsData || { data: [], total: 0 };
  }

  useEffect(() => {
    return () => {
      surveys = undefined;
      companies = undefined;
    };
  }, []);

  const onAddOrUpdate = async (param?: SurveyEvent) => {
    if (!surveys || !companies) {
      await load(true);
    }
    return window.webApp.setTab<SurveyEvent>({
      title: `${param ? "Update" : "New"} Survey Event`,
      element: <SurveyEventForm surveys={surveys} companies={companies} ref={formRef} defaultValues={param} />,
      onOk: async () => {
        if (!formRef.current) {
          throw new Error("Form creation error, please refresh or contact us!");
        }
        const value = await formRef.current.onSubmit();
        const res = param ? await updateSurveyEvent({ ...param, ...value }) : await addSurveyEvent(value);
        res.survey = surveys?.find((s) => s.id === res.surveyId);
        res.company = companies?.find((s) => s.id === res.companyId);
        return res;
      },
      show: true,
    });
  };

  const onDelete = (param: SurveyEvent) => {
    return deleteSurveyEvent(param.id);
  };

  const onSearch = async (value: string, original: SurveyEvent[]) => {
    if (original) {
      return { data: original.filter((a) => filterOnKeys(a, ["survey.name"], value)) };
    } else {
      return { data: [] };
    }
  };

  const renderActions = useCallback((record: SurveyEvent) => {
    return [
      {
        label: "CSV",
        icon: <DescriptionIcon />,
        tooltip: "Click to export CSVs",
        onClick: async () => {
          const { data } = await getCompanyStoresByCompanyId(record.companyId, {
            isAll: true,
            attributes: ["pk", "name", "storeId"],
          });
          const records = [
            ["Url", "Store Name", "Store Id"],
            ...(data || []).map((store) => [
              `${process.env.REACT_APP_SURVEY_URL}/#/survey/${record.id}?storeId=${store.id}`,
              store.name,
              store.storeId,
            ]),
          ];
          const csv = records.map((row: string[]) => row.join(",")).join("\n");
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          downloadFile("stores.csv", blob);
        },
      },
      {
        label: "QRs",
        icon: <QrCode2Icon />,
        tooltip: "Click to export QRs",
        onClick: async () => {
          const { data } = await getCompanyStoresByCompanyId(record.companyId, {
            isAll: true,
            attributes: ["pk", "name", "storeId"],
          });
          const stores = (data || []).map((store) => ({
            url: `${process.env.REACT_APP_SURVEY_URL}/#/survey/${record.id}?storeId=${store.id}`,
            name: store.name,
            storeId: store.storeId,
          }));
          const jszip = new JSZip();
          const qrs = jszip.folder("QRs");
          if (!qrs) {
            throw new Error("Failed to create folder");
          }
          for await (const store of stores) {
            const filename = `${store.name}-${store.storeId}.png`.replaceAll("/", "_");
            const canvas = await QRCode.toCanvas(store.url, { width: 2048 });
            const blob = await new Promise<Blob>((resolve, reject) =>
              canvas.toBlob((blob) => {
                if (blob) resolve(blob);
                else reject(new Error("Failed to create blob"));
              })
            );
            qrs.file(filename, blob);
          }
          const zipBlob = await jszip.generateAsync({ type: "blob" });
          downloadFile("qrs.zip", zipBlob);
        },
      },
      {
        label: "Coupons",
        icon: <LocalOfferIcon />,
        tooltip: "Click to create/import coupons",
        onClick: async () => {
          // Set timeout to ensure tab opens after action menu has closed
          setTimeout(() => {
            window.webApp.setTab({
              title: `Survey "${record?.survey?.name}" Event Coupons`,
              element: <SurveyCoupons surveyEventId={record?.id} />,
              onCancel: async () => {},
              cancelText: "Close",
              show: true,
            });
          }, 100);
        },
      },
    ];
  }, []);

  return (
    <TableList<SurveyEvent>
      headCells={headCells}
      onAddBtnText="New Survey Event"
      uid="id"
      onAdd={onAddOrUpdate}
      onSearch={onSearch}
      searchText="Search by name"
      onEdit={onAddOrUpdate}
      onDelete={onDelete}
      onLoad={load}
      filters={filters}
      actions={renderActions}
      initialSortKey="createdAt"
      initialOrder="desc"
    />
  );
}
